/**
 * Resolve specific product ids to products, for the wishlist screens.
 *
 * WHY NOT useActiveProducts(): that streams the whole catalogue, so its cost
 * grows with the number of products (fine for the Sync Monitor, wasteful here).
 * The wishlist screens only ever need the handful of products someone actually
 * saved, so this fetches exactly those — reads scale with saves (tens), not with
 * the catalogue (thousands). One-shot `getDocs`, not a listener: a saved
 * product's spec sheet does not need to update live in an admin report.
 *
 * Firestore allows at most 30 values in an `in` filter, so ids are chunked.
 */
import { useEffect, useMemo, useState } from 'react';
import { documentId, getDocs, query, where } from 'firebase/firestore';
import type { Product } from '@mitang/shared';
import { productsCol } from '../lib/collections';

const CHUNK = 30;

export function useProductsByIds(ids: string[]): Map<string, Product> {
  // Stable key so the effect doesn't re-run when the array identity changes but
  // its contents don't.
  const key = useMemo(() => [...new Set(ids)].sort().join('|'), [ids]);
  const [byId, setById] = useState<Map<string, Product>>(new Map());

  useEffect(() => {
    const wanted = key ? key.split('|') : [];
    if (wanted.length === 0) {
      setById(new Map());
      return;
    }
    let cancelled = false;

    const chunks: string[][] = [];
    for (let i = 0; i < wanted.length; i += CHUNK) chunks.push(wanted.slice(i, i + CHUNK));

    void Promise.all(
      chunks.map((chunk) =>
        getDocs(query(productsCol, where(documentId(), 'in', chunk))).catch(() => null),
      ),
    ).then((snaps) => {
      if (cancelled) return;
      const map = new Map<string, Product>();
      for (const snap of snaps) {
        for (const d of snap?.docs ?? []) map.set(d.id, d.data() as Product);
      }
      setById(map);
    });

    return () => {
      cancelled = true;
    };
  }, [key]);

  return byId;
}
